/**
 * SentEmailsView — the shared body of the Sent Emails feed.
 *
 *  A focused list of every email a surface has produced, with KPI
 *  cards, Type / Status / date filters, and a click-through preview of
 *  the exact message that was delivered. Two pages render it against
 *  different scopes via the `apiBase` prop:
 *
 *    • Super Admin "Sent Emails"      → `/super-admin/email-logs`
 *      (every tenant's mail)
 *    • Company Admin "Email Status Logs" → `/admin/email-logs`
 *      (scoped to the signed-in admin's own tenant)
 *
 *  Both endpoints return the same `{ counts }` and `{ data, pagination }`
 *  shapes (the admin counts are folded server-side to match), so the view
 *  is identical apart from where the data comes from. Filters, search and
 *  pagination all run SERVER-side, so every record is reachable regardless
 *  of volume and the footer total always matches the KPI card.
 *
 *  The page wrappers own their own Breadcrumb and title — this
 *  component is just the KPI strip + filters + table + preview.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Mail, ScrollText, Send, AlertCircle, Clock, Ban } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import {
  emailLogsResponse,
  emailLogCountsTabResponse,
  emailLogDetail,
} from '../lib/types'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { FilterRow } from './filters/FilterRow'
import { Pagination } from './ui/Pagination'
import { FloatingXScrollbar } from './table/FloatingXScrollbar'
import { Avatar } from './Avatar'
import { formatDateTime, formatNumber } from '../lib/format'
import { usePageSize } from '../lib/display-prefs'

function fmtDateTime(iso) {
  if (!iso) return '—'
  return formatDateTime(iso)
}

// Pills group by delivery outcome: green = succeeded (sent/delivered),
// amber = in-flight (pending/queued), red = problem (failed/bounced),
// muted grey = skipped (no template was wired up — not a failure). The
// label text distinguishes the two statuses that share each colour.
const LOG_STATUS_PILL = {
  sent: 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  delivered:
    'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  failed:
    'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]',
  bounced:
    'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]',
  pending:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  queued:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  // `skipped` rows are not delivery failures — they're "we tried to
  // fire an event but no template was wired up". Use a neutral muted
  // pill so the admin scans past sent/failed first and skipped
  // doesn't read as red-alarm.
  skipped:
    'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
}

// Type / Date options. Type values match the server-side enum in
// `utils/email-classify.js`; the Email type column on each row renders
// the same dimension as a tinted badge.
const LOG_TYPE_OPTIONS = [
  { value: 'all', label: 'All email types' },
  { value: 'system', label: 'System' },
  { value: 'notification', label: 'Notification' },
  { value: 'invitation', label: 'Invitation' },
  { value: 'password', label: 'Password reset' },
  { value: 'billing', label: 'Billing' },
  { value: 'verification', label: 'Verification' },
]
const LOG_DATE_PRESETS = [
  { value: 'all', label: 'All time' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

/** Resolve a "last N" preset to the ISO timestamp the API expects as
 *  its `from` bound. `all` (and anything unknown) means no lower bound. */
function logPresetToIso(preset) {
  if (!preset || preset === 'all') return null
  const hours = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 }[preset]
  if (!hours) return null
  return new Date(Date.now() - hours * 3600 * 1000).toISOString()
}

// Tinted pill per email type — same swatches + labels as every other
// Sent Emails surface so the two views read the type the same way.
// Unknown / null types fall back to the neutral `system` styling.
const LOG_TYPE_BADGE = {
  system: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10',
  notification: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
  invitation: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-white/[0.06] dark:text-teal-300 dark:border-white/10',
  password: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
  billing: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
  verification: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
}

function TypeBadge({ emailType }) {
  const type = emailType ?? 'system'
  const label = type.charAt(0).toUpperCase() + type.slice(1)
  return (
    <span
      className={
        'inline-flex items-center text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded border ' +
        (LOG_TYPE_BADGE[type] ?? LOG_TYPE_BADGE.system)
      }
    >
      {label}
    </span>
  )
}

export function SentEmailsView({ apiBase }) {
  // Scroll container for the wide log grid — drives the floating horizontal
  // scrollbar pinned to the viewport bottom (matches the admin tables).
  const logScrollRef = useRef(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [datePreset, setDatePreset] = useState('all')
  const [search, setSearch] = useState('')
  const [previewLogId, setPreviewLogId] = useState(null)
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()

  // Debounce the search box so typing paginates server-side without firing
  // a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  // Shared server-side filter params. The grid and the KPI counts send the
  // SAME status / type / date / search filter so the cards can never
  // contradict the grid below.
  function filterParams() {
    const params = {}
    if (statusFilter !== 'all') params.status = statusFilter
    if (typeFilter !== 'all') params.emailType = typeFilter
    const fromIso = logPresetToIso(datePreset)
    if (fromIso) params.from = fromIso
    if (debouncedSearch) params.search = debouncedSearch
    return params
  }

  const query = useQuery({
    queryKey: [
      'email-logs',
      apiBase,
      statusFilter,
      typeFilter,
      datePreset,
      debouncedSearch,
      page,
      limit,
    ],
    queryFn: async () => {
      const res = await api.get(apiBase, {
        params: { ...filterParams(), page, limit },
      })
      return emailLogsResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  // KPI counts honour the currently-applied status / type / date / search
  // filter (the same params the grid sends) so the cards can never
  // contradict the grid below.
  const countsQuery = useQuery({
    queryKey: [
      'email-logs',
      apiBase,
      'counts',
      statusFilter,
      typeFilter,
      datePreset,
      debouncedSearch,
    ],
    queryFn: async () => {
      const res = await api.get(`${apiBase}/counts`, { params: filterParams() })
      return emailLogCountsTabResponse.parse(res.data)
    },
  })
  const counts = countsQuery.data?.counts ?? {
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
    skipped: 0,
  }

  // Server-paginated: the endpoint returns exactly this page plus the TRUE
  // total, so we render the page as-is (no client-side slice) and the footer
  // count always matches the KPI card — and every record stays reachable.
  const pageRows = query.data?.data ?? []
  const total = query.data?.pagination?.total ?? 0
  const totalPages = query.data?.pagination?.totalPages ?? 1
  const safePage = Math.min(page, totalPages)

  // Snap back to the first page whenever the filters, search or page size
  // change, so the user never lands on a now-empty trailing page.
  useEffect(() => {
    setPage(1)
  }, [statusFilter, typeFilter, datePreset, debouncedSearch, limit])

  const hasFilters =
    statusFilter !== 'all' ||
    typeFilter !== 'all' ||
    datePreset !== 'all' ||
    search.trim() !== ''
  function clearFilters() {
    setStatusFilter('all')
    setTypeFilter('all')
    setDatePreset('all')
    setSearch('')
  }

  // `min-w-[1080px] lg:min-w-0` inside an `overflow-x-auto` wrapper — the
  // same idiom Audit Logs and Support Tickets use for their wide tables.
  //
  // Without it the three flexible tracks are `minmax(0, Nfr)`, so their
  // explicit 0 minimum let them collapse to ZERO width on a phone: the
  // fixed tracks alone (150 + 120 + 100, plus 60px of gaps and 32px of
  // padding) already exceed a 360px viewport, so Recipient, Subject and
  // Template rendered at 0px and the remainder was clipped by the
  // wrapper's `overflow-hidden` — columns missing, headers colliding
  // (QA, 2026-09-01). Below `lg` the table now scrolls horizontally and
  // keeps its proportions; at `lg` and up the min-width is dropped so the
  // desktop layout is byte-for-byte unchanged.
  const cols =
    'grid grid-cols-[64px_150px_minmax(0,1.1fr)_minmax(0,1.4fr)_120px_minmax(0,1fr)_100px] items-center gap-3 min-w-[1160px] lg:min-w-0'

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-5">
        <LogKpiCard
          icon={Mail}
          label="All emails"
          value={counts.total}
          tone="sky"
          loading={countsQuery.isLoading}
        />
        <LogKpiCard
          icon={Send}
          label="Sent"
          value={counts.sent}
          tone="emerald"
          loading={countsQuery.isLoading}
        />
        <LogKpiCard
          icon={Clock}
          label="Pending"
          value={counts.pending}
          tone="amber"
          loading={countsQuery.isLoading}
        />
        <LogKpiCard
          icon={Ban}
          label="Skipped"
          value={counts.skipped}
          tone="violet"
          loading={countsQuery.isLoading}
        />
        <LogKpiCard
          icon={AlertCircle}
          label="Failed"
          value={counts.failed}
          tone="rose"
          loading={countsQuery.isLoading}
        />
      </div>

      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search Recipient or Subject…"
        />
        {/* Only the statuses any send path actually writes are
         *  offered. `writeEmailLog` stamps sent | failed at creation
         *  and sendByEvent stamps skipped — nothing leaves a row as
         *  delivered / queued / bounced / pending (those are
         *  webhook/queue-driven states with no writer on this deploy),
         *  so listing them would just be dead filter options that
         *  always return zero rows. */}
        <FilterRow.Select
          ariaLabel="Status"
          placeholder="All statuses"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'sent', label: 'Sent' },
            { value: 'pending', label: 'Pending' },
            { value: 'skipped', label: 'Skipped' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
        <FilterRow.Select
          ariaLabel="Email type"
          placeholder="All email types"
          value={typeFilter}
          onChange={setTypeFilter}
          options={LOG_TYPE_OPTIONS}
        />
        <FilterRow.Select
          ariaLabel="Date range"
          placeholder="All time"
          value={datePreset}
          onChange={setDatePreset}
          options={LOG_DATE_PRESETS}
        />
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasFilters} onClick={clearFilters} />
      </FilterRow>

      {query.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : query.isError ? (
        <ErrorBlock message={extractApiError(query.error).message} />
      ) : total === 0 ? (
        <EmptyBlock
          icon={ScrollText}
          title="No emails to show"
          message="Emails will appear here with their recipient, template and delivery status."
        />
      ) : (
        <div
          ref={logScrollRef}
          data-fd-xscroll
          className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-x-auto"
        >
          <div
            className={`${cols} px-4 py-2 text-sm text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]`}
          >
            <span>SR. No.</span>
            <span>Sent At</span>
            <span>Recipient</span>
            <span>Subject</span>
            <span>Email Type</span>
            <span>Template</span>
            <span>Status</span>
          </div>
          {pageRows.map((log, idx) => (
            <button
              key={log.id}
              type="button"
              onClick={() => setPreviewLogId(log.id)}
              className={`${cols} w-full px-4 py-3 border-b border-[color:var(--color-rule)] last:border-b-0 text-left hover:bg-[color:var(--color-card-hover)] cursor-pointer transition-colors`}
              title="Click to view the email that was sent"
            >
              {/* SR. No. — page-offset positional index (tabular figures so the
               *  digits align), matching the Audit Log's leading serial column. */}
              <span className="text-sm text-[color:var(--color-ink-muted)] tabular-nums">
                {(safePage - 1) * limit + idx + 1}
              </span>
              <span className="text-xs text-[color:var(--color-ink-muted)]">
                {fmtDateTime(log.sentAt ?? log.createdAt)}
              </span>
              <span className="flex items-center gap-2 min-w-0">
                <Avatar
                  name={log.recipientName || log.recipient}
                  avatarUrl={log.avatarUrl ?? null}
                  size="sm"
                />
                <span className="text-sm text-[color:var(--color-ink-strong)] truncate">
                  {log.recipient}
                </span>
              </span>
              <span
                className="text-sm text-[color:var(--color-ink-muted)] truncate"
                title={log.subject ?? undefined}
              >
                {log.subject ?? '—'}
              </span>
              <span>
                <TypeBadge emailType={log.emailType} />
              </span>
              <span className="text-sm text-[color:var(--color-ink-muted)] truncate">
                {log.templateName ?? log.templateKey ?? '—'}
              </span>
              <span
                className={
                  'text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded w-fit ' +
                  (LOG_STATUS_PILL[log.status] ?? LOG_STATUS_PILL.pending)
                }
                title={log.error ?? undefined}
              >
                {log.status}
              </span>
            </button>
          ))}
          <Pagination
            page={safePage}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={(n) => {
              setLimit(n)
              setPage(1)
            }}
          />
        </div>
      )}
      <FloatingXScrollbar containerRef={logScrollRef} />
      {previewLogId ? (
        <SentEmailPreviewDialog
          apiBase={apiBase}
          logId={previewLogId}
          onClose={() => setPreviewLogId(null)}
        />
      ) : null}
    </div>
  )
}

/** Tone palette for the Sent Emails KPI cards — identical swatches to
 *  the platform dashboard / All Users `KpiCard` so the strip reads as
 *  the same visual family across every Super Admin surface. */
const LOG_KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
}

/** Single-stat KPI card for the Sent Emails page — matches the platform
 *  dashboard `KpiCard` beat-for-beat: rounded-xl chrome, blurred halo
 *  bleed in the top-right, 12×12 tinted icon chip, 3xl font-display
 *  value, uppercase tracking-wider label, and a pulsing skeleton during
 *  the initial counts fetch. */
function LogKpiCard({ icon: Icon, label, value, tone = 'violet', loading }) {
  const tones = LOG_KPI_TONES[tone] ?? LOG_KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          {loading ? (
            <div className="mt-3 h-7 w-14 rounded animate-pulse bg-[color:var(--color-rule)]" />
          ) : (
            <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
              {formatNumber(Number(value ?? 0))}
            </p>
          )}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

/** Preview the exact message a recipient received — the html + text
 *  body are stored as a snapshot on the EmailLog row when the message
 *  is sent, so this view is true to what landed in the inbox even if
 *  the template was edited afterwards.
 *
 *  Fetches on open (the list response strips html/text for payload
 *  size — see `EmailLogs.list` on the API), then renders the html in
 *  a sandboxed iframe via srcDoc so any styles + images inside the
 *  template can't escape and touch the surrounding admin page. */
function SentEmailPreviewDialog({ apiBase, logId, onClose }) {
  // Natural height of the rendered email, measured once the iframe
  // loads — see the iframe below for why it must not scroll itself.
  const [frameHeight, setFrameHeight] = useState(480)
  // Natural WIDTH, for the same reason. null = content fits, stay fluid.
  const [frameWidth, setFrameWidth] = useState(null)
  const detail = useQuery({
    queryKey: ['email-logs', apiBase, logId],
    queryFn: async () => {
      const res = await api.get(`${apiBase}/${encodeURIComponent(logId)}`)
      return emailLogDetail.parse(res.data)
    },
  })
  const log = detail.data
  const previewHtml = useMemo(() => {
    if (!log?.html) return null
    return `<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0">${log.html}</body></html>`
  }, [log?.html])
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title="Sent Email Preview"
      description="The exact message that was delivered — variables already substituted."
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {detail.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : detail.isError ? (
        <ErrorBlock message={extractApiError(detail.error).message} />
      ) : !log ? null : (
        <div className="space-y-3">
          <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
            <span className="text-[color:var(--color-ink-muted)] uppercase tracking-wider text-xs font-semibold">
              To
            </span>
            <span className="text-[color:var(--color-ink-strong)] break-all">
              {log.recipient}
            </span>
            <span className="text-[color:var(--color-ink-muted)] uppercase tracking-wider text-xs font-semibold">
              Subject
            </span>
            <span className="text-[color:var(--color-ink-strong)]">
              {log.subject ?? '—'}
            </span>
            <span className="text-[color:var(--color-ink-muted)] uppercase tracking-wider text-xs font-semibold">
              Template
            </span>
            <span className="text-[color:var(--color-ink)]">
              {log.templateName ?? log.templateKey ?? '—'}
            </span>
            <span className="text-[color:var(--color-ink-muted)] uppercase tracking-wider text-xs font-semibold">
              Sent at
            </span>
            <span className="text-[color:var(--color-ink)]">
              {fmtDateTime(log.sentAt ?? log.createdAt)}
            </span>
            <span className="text-[color:var(--color-ink-muted)] uppercase tracking-wider text-xs font-semibold">
              Status
            </span>
            <span
              className={
                'text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded w-fit ' +
                (LOG_STATUS_PILL[log.status] ?? LOG_STATUS_PILL.pending)
              }
            >
              {log.status}
            </span>
          </div>
          {log.error ? (
            <div className="rounded-lg border border-[color:var(--color-priority-urgent)] bg-[color:var(--color-priority-urgent-bg,#fef2f2)] p-3 text-sm text-[color:var(--color-priority-urgent)]">
              <p className="font-semibold mb-1">Delivery failed</p>
              <p className="break-all">{log.error}</p>
            </div>
          ) : null}
          <div>
            <p className="text-[color:var(--color-ink-muted)] uppercase tracking-wider text-xs font-semibold mb-2">
              Body
            </p>
            {previewHtml ? (
              // Auto-sized to the email's natural height so the iframe
              // NEVER scrolls internally — the modal body is the single
              // scroller. The old fixed 480px window nested a third
              // scrollbar (page → modal → iframe) and wheel events over
              // the email got trapped in the inner document, which read
              // as "scrolling lags" (QA-reported). `allow-same-origin`
              // WITHOUT allow-scripts lets the parent measure the
              // content height; scripts still cannot run inside the
              // snapshot, so it can't touch the surrounding admin page.
              // `pointer-events-none` — the preview is a PICTURE of the
              // email, not a live document: without it the CTA buttons
              // (e.g. "Open Task →") were hoverable and clickable, and a
              // click navigated the sandboxed frame to the target URL
              // (QA-reported). It also lets wheel events fall through to
              // the modal — the single scroller.
              // The wrapper is the horizontal scroller. Most templates carry
              // a fixed-width table (600px is the email convention), so on a
              // narrow dialog the IFRAME's own document overflowed and painted
              // its own horizontal scrollbar — which `pointer-events-none`
              // made impossible to grab, so left/right scrolling simply did
              // nothing (QA, 2026-09-01). Sizing the frame to its content and
              // scrolling this element instead gives a real, draggable
              // scrollbar, and keeps the "iframe never scrolls itself" rule
              // the height measurement above already follows.
              <div className="overflow-x-auto rounded-lg border border-[color:var(--color-rule)] bg-white">
                <iframe
                  title="Sent email body"
                  srcDoc={previewHtml}
                  sandbox="allow-same-origin"
                  onLoad={(e) => {
                    const el = e.target
                    const root = el.contentDocument?.documentElement
                    if (!root) return
                    // Height is read at the CURRENT (narrower) width. If the
                    // frame then widens, the content reflows shorter — so this
                    // is an over-estimate, never an under-estimate, and no
                    // internal vertical scrollbar can appear.
                    const h = root.scrollHeight
                    if (h) setFrameHeight(h + 16)
                    // Only pin a width when the email is genuinely wider than
                    // the frame; otherwise stay fluid so a responsive template
                    // still fills the dialog as before.
                    const sw = root.scrollWidth
                    setFrameWidth(sw > el.clientWidth + 1 ? sw : null)
                  }}
                  style={{ height: frameHeight, width: frameWidth ?? '100%' }}
                  className="block bg-white pointer-events-none"
                />
              </div>
            ) : log.text ? (
              // Plain-text fallback — no inner max-h/overflow for the
              // same single-scroller reason as the iframe above.
              <pre className="w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3 text-sm whitespace-pre-wrap font-mono">
                {log.text}
              </pre>
            ) : (
              <p className="text-sm text-[color:var(--color-ink-muted)] italic">
                No body was captured for this send (older log row, or the
                email was skipped before it could be rendered).
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

function EmptyBlock({ icon: Icon, title, message }) {
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
      <Icon size={28} className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]" />
      <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
        {title}
      </p>
      <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] max-w-md mx-auto">
        {message}
      </p>
    </div>
  )
}

function ErrorBlock({ message }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] p-6 flex items-start gap-3">
      <AlertCircle
        size={18}
        className="shrink-0 mt-0.5 text-[color:var(--color-danger)]"
      />
      <div>
        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
          Couldn't load this data
        </p>
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
          {message}
        </p>
      </div>
    </div>
  )
}
